import { extractFuturesRoot } from '../../constants/contractMultipliers';
import { isIndexOrCfdInstrument } from '../../constants/indexInstruments';
import { isReplayTimeframeDisabled } from '../trades/replayRange';

export type TradeChartAssetClass = 'STK' | 'FUT' | 'CASH' | 'CRYPTO';
export type TradeChartMode = 'authoritative' | 'approximate' | 'unavailable';
export type TradeChartSourceType = 'exact_contract' | 'same_symbol_coarser_tf' | 'continuous_contract' | 'context_only' | 'none';
export type TradeChartTimeframe = '1m' | '5m' | '15m' | '30m' | '1h' | '1d';

export type TradeChartResolutionResult = {
  chartMode: TradeChartMode;
  assetClass: TradeChartAssetClass;
  requestedSymbol: string;
  resolvedSymbol: string;
  requestedTimeframe: TradeChartTimeframe;
  resolvedTimeframe: TradeChartTimeframe;
  exactInstrument: boolean;
  exactTimeframe: boolean;
  barsAvailable: boolean;
  replayAllowed: boolean;
  metricsAllowed: boolean;
  fallbackReason: string | null;
  warningCode: string | null;
  warningMessage: string | null;
  sourceType: TradeChartSourceType;
  requestedLocalSymbol?: string | null;
  resolvedLocalSymbol?: string | null;
  requestedContractMonth?: string | null;
  resolvedContractMonth?: string | null;
  resolvedConid?: string | null;
};

export type TradeChartFetchAttempt = {
  assetClass: TradeChartAssetClass;
  chartMode: Exclude<TradeChartMode, 'unavailable'>;
  sourceType: Exclude<TradeChartSourceType, 'none'>;
  symbol: string;
  timeframe: TradeChartTimeframe;
  exactInstrument: boolean;
  fallbackReason: string | null;
  qualifiers?: {
    localSymbol?: string;
    lastTradeDateOrContractMonth?: string;
    conid?: string;
  };
};

export type TradeChartFetchPayload = {
  error?: boolean;
  code?: string;
  reason?: string;
  source?: string;
  fallbackReason?: string;
  status?: number;
  message?: string;
  meta?: Record<string, unknown> | null;
  [k: string]: unknown;
};

export type TradeChartStateBadgeKind = 'live' | 'cached' | 'fresh' | 'fallback' | 'unavailable';

export type TradeChartStateBadgeModel = {
  kind: TradeChartStateBadgeKind;
  label: string;
  title: string;
};

export type TradeChartLoadResult<TCandle = unknown> = {
  resolution: TradeChartResolutionResult;
  candles: TCandle[];
  payload: TradeChartFetchPayload | null;
};

export type ResolveTradeChartSourceInput<TCandle = unknown> = {
  assetClass: string;
  requestedSymbol: string;
  requestedTimeframe: string;
  instrumentType?: string | null;
  exactContract?: {
    localSymbol?: string | null;
    lastTradeDateOrContractMonth?: string | null;
    conid?: string | number | null;
  };
  fetchBars: (attempt: TradeChartFetchAttempt) => Promise<{
    candles: TCandle[];
    payload: TradeChartFetchPayload | null;
  }>;
};

const INTRADAY_FALLBACK_ORDER: TradeChartTimeframe[] = ['1m', '5m', '15m', '30m', '1h'];

export function normalizeTradeChartAssetClass(value: string): TradeChartAssetClass {
  const raw = String(value || '').trim().toUpperCase();
  if (raw === 'FUT' || raw === 'FUTURE' || raw === 'FUTURES') return 'FUT';
  if (raw === 'CASH' || raw === 'FX' || raw === 'FOREX' || raw === 'CURRENCY') return 'CASH';
  if (raw === 'CRYPTO' || raw === 'CRYPTOCURRENCY') return 'CRYPTO';
  return 'STK';
}

export function normalizeTradeChartTimeframe(value: string): TradeChartTimeframe {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === '1d' || raw === 'day' || raw === '1day') return '1d';
  if (raw === '1h' || raw === '60m' || raw === '60min' || raw === '60') return '1h';
  if (raw === '30m' || raw === '30min' || raw === '30') return '30m';
  if (raw === '15m' || raw === '15min' || raw === '15') return '15m';
  if (raw === '5m' || raw === '5min' || raw === '5') return '5m';
  return '1m';
}

export function toRequestTimeframe(value: TradeChartTimeframe): '1min' | '5min' | '15min' | '30min' | '60min' | '1d' {
  if (value === '5m') return '5min';
  if (value === '15m') return '15min';
  if (value === '30m') return '30min';
  if (value === '1h') return '60min';
  if (value === '1d') return '1d';
  return '1min';
}

export function getTimeframeFallbackOrder(_assetClass: string, requestedTimeframe: string, includeDailyContext = false): TradeChartTimeframe[] {
  const normalizedTf = normalizeTradeChartTimeframe(requestedTimeframe);
  if (normalizedTf === '1d') return ['1d'];

  const baseOrder = [...INTRADAY_FALLBACK_ORDER];
  const startIndex = Math.max(0, baseOrder.indexOf(normalizedTf));
  const result = baseOrder.slice(startIndex);

  if (includeDailyContext) result.push('1d');

  return Array.from(new Set(result));
}

export function canUseBarsForReplay(resolution: Pick<TradeChartResolutionResult, 'chartMode' | 'barsAvailable' | 'resolvedTimeframe'>): boolean {
  return resolution.chartMode === 'authoritative'
    && resolution.barsAvailable
    && !isReplayTimeframeDisabled(resolution.resolvedTimeframe);
}

export function canUseBarsForDeterministicMetrics(resolution: Pick<TradeChartResolutionResult, 'chartMode' | 'barsAvailable' | 'resolvedTimeframe'>): boolean {
  return resolution.chartMode === 'authoritative' && resolution.barsAvailable;
}

export function getChartWarningMessage(resolution: Pick<TradeChartResolutionResult, 'chartMode' | 'assetClass' | 'requestedTimeframe' | 'resolvedTimeframe' | 'sourceType' | 'barsAvailable' | 'fallbackReason'> & { warningCode?: string | null }): string | null {
  if (!resolution.barsAvailable || resolution.chartMode === 'unavailable') {
    if (resolution.fallbackReason === 'index_chart_data_unavailable' || resolution.warningCode === 'INDEX_CHART_UNAVAILABLE') {
      return 'Chart data unavailable for this instrument.';
    }
    if (resolution.assetClass === 'FUT') {
      if (resolution.fallbackReason === 'missing_exact_futures_contract') {
        return 'Exact futures contract metadata is missing; authoritative replay is unavailable.';
      }
      return 'No usable historical bars available for the exact futures trade chart.';
    }
    return 'No usable historical bars available for this trade chart.';
  }

  if (resolution.sourceType === 'same_symbol_coarser_tf' && resolution.requestedTimeframe !== resolution.resolvedTimeframe) {
    return `${resolution.requestedTimeframe} history unavailable for the exact instrument; using ${resolution.resolvedTimeframe} bars.`;
  }

  if (resolution.assetClass === 'FUT' && (resolution.sourceType === 'continuous_contract' || resolution.sourceType === 'context_only')) {
    return 'Using approximate futures context chart; exact contract replay and deterministic metrics are disabled.';
  }

  if (resolution.chartMode === 'approximate') {
    return 'Using approximate context bars; replay and deterministic metrics are disabled.';
  }

  return null;
}

type TradeChartLoadMessageContext = {
  meta?: Record<string, unknown> | null;
  resolution?: Pick<TradeChartResolutionResult, 'assetClass'> | null;
};

function readTradeChartLoadMessageAssetClass(meta: Record<string, unknown>, resolution?: Pick<TradeChartResolutionResult, 'assetClass'> | null): TradeChartAssetClass | null {
  if (resolution?.assetClass) return normalizeTradeChartAssetClass(resolution.assetClass);

  const chartResolution = meta.chartResolution;
  if (chartResolution && typeof chartResolution === 'object') {
    const nestedAssetClass = readMetaString(chartResolution as Record<string, unknown>, 'assetClass');
    if (nestedAssetClass) return normalizeTradeChartAssetClass(nestedAssetClass);
  }

  const metaAssetClass = readMetaString(meta, 'assetClass');
  if (metaAssetClass) return normalizeTradeChartAssetClass(metaAssetClass);

  return null;
}

function formatTradeChartTechnicalDetailTitle(message: string): string {
  const trimmed = String(message || '').trim();
  if (!trimmed) return 'Technical detail:';
  return trimmed.endsWith('.') ? `Technical detail: ${trimmed}` : `Technical detail: ${trimmed}.`;
}

export function formatTradeChartLoadMessage(rawMessage: string | null | undefined, context: TradeChartLoadMessageContext = {}): { text: string; title?: string } {
  const message = String(rawMessage ?? '').trim();
  if (!message) return { text: '' };

  const meta = context.meta && typeof context.meta === 'object' ? context.meta : {};
  const source = readMetaString(meta, 'source');
  const providerKey = readMetaString(meta, 'providerKey');
  const assetClass = readTradeChartLoadMessageAssetClass(meta, context.resolution ?? null);

  const safeText = assetClass === 'FUT'
    ? 'Chart data unavailable. Futures replay requires connected IBKR/local market data.'
    : (source === 'ibkr' || providerKey === 'ibkr')
      ? 'Chart data unavailable. This environment is not connected to IBKR market data.'
      : ((context.resolution || meta.chartResolution || source || providerKey)
        ? 'Chart data unavailable. Historical market data is currently unavailable for this symbol/timeframe.'
        : 'Chart data unavailable. This environment is not connected to IBKR market data.');

  const ibkrMatch = message.match(/^IBKR is not reachable at\s+(.+?)(?:\s+\(([^)]+)\))?$/i);
  if (ibkrMatch) {
    const address = ibkrMatch[1].trim();
    const reason = ibkrMatch[2]?.trim();
    return {
      text: safeText,
      title: formatTradeChartTechnicalDetailTitle(`IBKR is not reachable at ${address}${reason ? ` (${reason})` : ''}`),
    };
  }

  const futuresMatch = message.match(/^No futures contract matching\s+(.+?)\s+for\s+(.+)$/i);
  if (futuresMatch) {
    return {
      text: safeText,
      title: formatTradeChartTechnicalDetailTitle(message),
    };
  }

  return {
    text: safeText,
    title: formatTradeChartTechnicalDetailTitle(message),
  };
}

export function getResolvedCandles<TCandle>(resolution: Pick<TradeChartResolutionResult, 'barsAvailable'>, candles: TCandle[]): TCandle[] {
  return resolution.barsAvailable ? [...(candles || [])] : [];
}

function readMetaString(meta: Record<string, unknown>, key: string): string | null {
  const value = meta[key];
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function readMetaBoolean(meta: Record<string, unknown>, key: string): boolean | null {
  const value = meta[key];
  return typeof value === 'boolean' ? value : null;
}

function compatibilityFallbackReasonCode(meta: Record<string, unknown>): string | null {
  const explicit = readMetaString(meta, 'fallbackReasonCode');
  if (explicit) return explicit;

  const legacy = readMetaString(meta, 'fallbackReason');
  if (legacy === 'throttled') return 'UPSTREAM_THROTTLED';
  return null;
}

function describeReasonCode(code: string | null): string {
  switch (code) {
    case 'CACHE_ONLY_REQUESTED':
      return 'Cache-only request missed Redis; upstream was not attempted.';
    case 'UPSTREAM_THROTTLED':
      return 'Fallback used because upstream was throttled.';
    case 'TRANSIENT_UPSTREAM_FAILURE':
      return 'Fallback used because upstream was temporarily unavailable.';
    case 'HMDS_NO_DATA':
    case 'NO_HISTORICAL_DATA':
      return 'No usable historical data was available for this chart.';
    case 'PROVIDER_INSUFFICIENT_DAILY':
      return 'Degraded chart state due to limited provider coverage.';
    case 'EMPTY_BARS':
      return 'The request completed but returned no usable bars.';
    case 'SUSPICIOUS_DATASET':
      return 'The provider returned unusable bars, so the chart was degraded.';
    case 'INVALID_CONTRACT_SMART':
      return 'Fallback used after retrying the contract resolution path.';
    default:
      return 'Current chart data state.';
  }
}

export function extractTradeChartMeta(
  payload: TradeChartFetchPayload | null,
  resolution?: TradeChartResolutionResult | null,
): Record<string, unknown> | null {
  const payloadMeta = payload?.meta && typeof payload.meta === 'object'
    ? { ...(payload.meta as Record<string, unknown>) }
    : {};

  const merged: Record<string, unknown> = { ...payloadMeta };

  if (typeof payload?.status === 'number' && Number.isFinite(payload.status)) merged.status = payload.status;
  if (payload?.error === true) merged.error = true;

  for (const key of ['message', 'code', 'reason', 'source', 'fallbackReason'] as const) {
    const value = payload?.[key];
    if ((merged[key] == null || merged[key] === '') && typeof value === 'string' && value.trim().length > 0) {
      merged[key] = value.trim();
    }
  }

  if (resolution) merged.chartResolution = resolution;

  return Object.keys(merged).length > 0 ? merged : null;
}

export function getTradeChartStateBadgeModel(opts: {
  meta?: Record<string, unknown> | null;
  resolution?: Partial<TradeChartResolutionResult> | null;
}): TradeChartStateBadgeModel | null {
  const meta = opts.meta && typeof opts.meta === 'object' ? opts.meta : {};
  const resolution = opts.resolution
    ?? ((meta as Record<string, unknown>).chartResolution as Partial<TradeChartResolutionResult> | null | undefined)
    ?? null;

  const requestMode = readMetaString(meta, 'requestMode');
  const source = readMetaString(meta, 'source');
  const cacheStatus = readMetaString(meta, 'cacheStatus');
  const outcome = readMetaString(meta, 'outcome');
  const code = readMetaString(meta, 'code');
  const reasonCode = readMetaString(meta, 'reasonCode');
  const fallbackReasonCode = compatibilityFallbackReasonCode(meta);
  const fallbackUsed = readMetaBoolean(meta, 'fallbackUsed') === true || outcome === 'fallback' || source === 'redis_fallback';
  const chartMode = resolution?.chartMode ?? null;
  const barsAvailable = resolution?.barsAvailable;

  const unavailableCode = code || reasonCode;
  const isUnavailable = unavailableCode === 'REDIS_MISS'
    || reasonCode === 'CACHE_ONLY_REQUESTED'
    || unavailableCode === 'NO_HISTORICAL_DATA'
    || outcome === 'miss'
    || chartMode === 'unavailable';

  if (isUnavailable) {
    const label = unavailableCode === 'NO_HISTORICAL_DATA' ? 'No Data' : 'Unavailable';
    return {
      kind: 'unavailable',
      label,
      title: describeReasonCode(unavailableCode === 'REDIS_MISS' ? 'CACHE_ONLY_REQUESTED' : unavailableCode),
    };
  }

  if (fallbackUsed) {
    return {
      kind: 'fallback',
      label: 'Fallback',
      title: describeReasonCode(fallbackReasonCode),
    };
  }

  if (chartMode === 'approximate') {
    return {
      kind: 'fallback',
      label: 'Approx',
      title: resolution?.warningMessage || 'Approximate chart context is in use.',
    };
  }

  if (requestMode === 'fresh' && (outcome === 'success' || source === 'ibkr' || barsAvailable === true)) {
    return {
      kind: 'fresh',
      label: 'Fresh',
      title: 'Explicit fresh fetch from the upstream market-data path.',
    };
  }

  if (source === 'redis' || cacheStatus === 'hit') {
    return {
      kind: 'cached',
      label: 'Cached',
      title: 'Served from Redis-backed cache.',
    };
  }

  if (source === 'ibkr' || outcome === 'success' || readMetaBoolean(meta, 'upstreamAttempted') === true || barsAvailable === true) {
    return {
      kind: 'live',
      label: 'Live',
      title: 'Loaded from the primary market-data path.',
    };
  }

  return null;
}

function normalizeOptionalString(value: unknown): string | null {
  const s = String(value ?? '').trim();
  return s ? s : null;
}

function deriveFuturesRoot(requestedSymbol: string, localSymbol?: string | null): string {
  const fromLocal = extractFuturesRoot(localSymbol || undefined);
  if (fromLocal) return fromLocal;
  const fromRequested = extractFuturesRoot(requestedSymbol || undefined);
  if (fromRequested) return fromRequested;
  return String(requestedSymbol || '').trim().toUpperCase();
}

function buildResolution(base: {
  assetClass: TradeChartAssetClass;
  requestedSymbol: string;
  resolvedSymbol: string;
  requestedTimeframe: TradeChartTimeframe;
  resolvedTimeframe: TradeChartTimeframe;
  chartMode: TradeChartMode;
  exactInstrument: boolean;
  barsAvailable: boolean;
  fallbackReason: string | null;
  sourceType: TradeChartSourceType;
  requestedLocalSymbol?: string | null;
  resolvedLocalSymbol?: string | null;
  requestedContractMonth?: string | null;
  resolvedContractMonth?: string | null;
  resolvedConid?: string | null;
}): TradeChartResolutionResult {
  const exactTimeframe = base.requestedTimeframe === base.resolvedTimeframe;
  const partial: TradeChartResolutionResult = {
    chartMode: base.chartMode,
    assetClass: base.assetClass,
    requestedSymbol: base.requestedSymbol,
    resolvedSymbol: base.resolvedSymbol,
    requestedTimeframe: base.requestedTimeframe,
    resolvedTimeframe: base.resolvedTimeframe,
    exactInstrument: base.exactInstrument,
    exactTimeframe,
    barsAvailable: base.barsAvailable,
    replayAllowed: false,
    metricsAllowed: false,
    fallbackReason: base.fallbackReason,
    warningCode: null,
    warningMessage: null,
    sourceType: base.sourceType,
    requestedLocalSymbol: base.requestedLocalSymbol ?? null,
    resolvedLocalSymbol: base.resolvedLocalSymbol ?? null,
    requestedContractMonth: base.requestedContractMonth ?? null,
    resolvedContractMonth: base.resolvedContractMonth ?? null,
    resolvedConid: base.resolvedConid ?? null,
  };

  partial.replayAllowed = canUseBarsForReplay(partial);
  partial.metricsAllowed = canUseBarsForDeterministicMetrics(partial);

  if (!partial.barsAvailable || partial.chartMode === 'unavailable') {
    partial.warningCode = base.fallbackReason === 'index_chart_data_unavailable'
      ? 'INDEX_CHART_UNAVAILABLE'
      : 'NO_USABLE_BARS';
  }
  else if (partial.sourceType === 'same_symbol_coarser_tf') partial.warningCode = 'TIMEFRAME_DEGRADED';
  else if (partial.assetClass === 'FUT' && partial.sourceType === 'continuous_contract') partial.warningCode = 'APPROXIMATE_CONTINUOUS_FUTURES';
  else if (partial.chartMode === 'approximate') partial.warningCode = 'APPROXIMATE_CONTEXT';

  partial.warningMessage = getChartWarningMessage(partial);
  return partial;
}

function shouldAttemptFallback(payload: TradeChartFetchPayload | null): boolean {
  if (!payload || typeof payload !== 'object') return false;

  const status = Number(payload.status);
  const meta = payload.meta && typeof payload.meta === 'object'
    ? payload.meta as Record<string, unknown>
    : null;

  const explicitCode = [
    payload.code,
    payload.reason,
    meta?.code,
    meta?.reasonCode,
    meta?.fallbackAllowedReasonCode,
  ].find((value) => typeof value === 'string' && String(value).trim().length > 0);
  const normalizedCode = typeof explicitCode === 'string' ? explicitCode.trim().toUpperCase() : null;
  const messageText = typeof payload.message === 'string' ? payload.message.trim().toUpperCase() : '';

  if (typeof meta?.fallbackAllowed === 'boolean') return meta.fallbackAllowed;

  if (
    normalizedCode === 'CONTRACT_NOT_FOUND'
    || normalizedCode === 'NO_HISTORICAL_DATA'
    || messageText.includes('CONTRACT NOT FOUND')
  ) {
    return false;
  }

  if (payload.error === true) return true;
  if (Number.isFinite(status) && status >= 500) return true;

  const allowChartFallback = meta?.allowChartFallback;
  if (allowChartFallback === true) return true;
  if (allowChartFallback === false) return false;

  const explicitReason = [
    payload.message,
    (payload as Record<string, unknown>).fallbackReason,
    (payload as Record<string, unknown>).chartFallbackReason,
    (payload as Record<string, unknown>).code,
    meta?.fallbackReason,
    meta?.chartFallbackReason,
    meta?.code,
  ].find((value) => typeof value === 'string' && String(value).trim().length > 0);

  return typeof explicitReason === 'string' && explicitReason.trim().length > 0;
}

async function executeAttempts<TCandle>(args: {
  attempts: TradeChartFetchAttempt[];
  assetClass: TradeChartAssetClass;
  requestedSymbol: string;
  requestedTimeframe: TradeChartTimeframe;
  exactContract?: ResolveTradeChartSourceInput<TCandle>['exactContract'];
  fetchBars: ResolveTradeChartSourceInput<TCandle>['fetchBars'];
  unavailableFallbackReason: string | null;
}): Promise<TradeChartLoadResult<TCandle>> {
  const requestedLocalSymbol = normalizeOptionalString(args.exactContract?.localSymbol);
  const requestedContractMonth = normalizeOptionalString(args.exactContract?.lastTradeDateOrContractMonth);
  let lastPayload: TradeChartFetchPayload | null = null;
  let fallbackUnlocked = false;

  for (let index = 0; index < args.attempts.length; index += 1) {
    const attempt = args.attempts[index];
    const response = await args.fetchBars(attempt);
    const payload = response?.payload ?? null;
    const candles = Array.isArray(response?.candles) ? response.candles : [];
    lastPayload = payload;

    if (Number(payload?.status) === 429) {
      const err: any = new Error(String(payload?.message || 'Rate limit exceeded'));
      err.status = 429;
      err.retryAfterMs = Number(payload?.retryAfterMs ?? payload?.meta?.retryAfterMs ?? 0);
      throw err;
    }

    if (candles.length > 0) {
      const resolvedLocalSymbol = normalizeOptionalString(attempt.qualifiers?.localSymbol) ?? requestedLocalSymbol;
      const resolvedSymbol = attempt.assetClass === 'FUT' && attempt.exactInstrument && resolvedLocalSymbol
        ? resolvedLocalSymbol
        : attempt.symbol;

      const resolution = buildResolution({
        assetClass: args.assetClass,
        requestedSymbol: args.requestedSymbol,
        resolvedSymbol,
        requestedTimeframe: args.requestedTimeframe,
        resolvedTimeframe: attempt.timeframe,
        chartMode: attempt.chartMode,
        exactInstrument: attempt.exactInstrument,
        barsAvailable: true,
        fallbackReason: attempt.fallbackReason,
        sourceType: attempt.sourceType,
        requestedLocalSymbol,
        resolvedLocalSymbol,
        requestedContractMonth,
        resolvedContractMonth: normalizeOptionalString(attempt.qualifiers?.lastTradeDateOrContractMonth) ?? requestedContractMonth,
        resolvedConid: normalizeOptionalString(attempt.qualifiers?.conid),
      });

      return { resolution, candles, payload };
    }

    if (shouldAttemptFallback(payload)) {
      fallbackUnlocked = true;
      continue;
    }

    if (index < args.attempts.length - 1 && !fallbackUnlocked) {
      break;
    }
  }

  const unavailable = buildResolution({
    assetClass: args.assetClass,
    requestedSymbol: args.requestedSymbol,
    resolvedSymbol: args.requestedSymbol,
    requestedTimeframe: args.requestedTimeframe,
    resolvedTimeframe: args.requestedTimeframe,
    chartMode: 'unavailable',
    exactInstrument: args.assetClass !== 'FUT' ? true : Boolean(requestedLocalSymbol || requestedContractMonth || args.exactContract?.conid),
    barsAvailable: false,
    fallbackReason: args.unavailableFallbackReason,
    sourceType: 'none',
    requestedLocalSymbol,
    resolvedLocalSymbol: requestedLocalSymbol,
    requestedContractMonth,
    resolvedContractMonth: requestedContractMonth,
    resolvedConid: normalizeOptionalString(args.exactContract?.conid),
  });

  return { resolution: unavailable, candles: [], payload: lastPayload };
}

export async function resolveFuturesTradeChartSource<TCandle>(input: ResolveTradeChartSourceInput<TCandle>): Promise<TradeChartLoadResult<TCandle>> {
  const assetClass: TradeChartAssetClass = 'FUT';
  const requestedSymbol = String(input.requestedSymbol || '').trim().toUpperCase();
  const requestedTimeframe = normalizeTradeChartTimeframe(input.requestedTimeframe);
  const requestedLocalSymbol = normalizeOptionalString(input.exactContract?.localSymbol);
  const requestedContractMonth = normalizeOptionalString(input.exactContract?.lastTradeDateOrContractMonth);
  const requestedConid = normalizeOptionalString(input.exactContract?.conid);
  const exactContractAvailable = Boolean(requestedLocalSymbol || requestedContractMonth || requestedConid);
  const exactOrder = getTimeframeFallbackOrder(assetClass, requestedTimeframe, false);
  const contextOrder = getTimeframeFallbackOrder(assetClass, requestedTimeframe, true);
  const root = deriveFuturesRoot(requestedSymbol, requestedLocalSymbol);
  const attempts: TradeChartFetchAttempt[] = [];

  if (exactContractAvailable) {
    for (const timeframe of exactOrder) {
      attempts.push({
        assetClass,
        chartMode: 'authoritative',
        sourceType: timeframe === requestedTimeframe ? 'exact_contract' : 'same_symbol_coarser_tf',
        symbol: requestedSymbol,
        timeframe,
        exactInstrument: true,
        fallbackReason: timeframe === requestedTimeframe ? null : 'same_contract_coarser_timeframe',
        qualifiers: {
          localSymbol: requestedLocalSymbol ?? undefined,
          lastTradeDateOrContractMonth: requestedContractMonth ?? undefined,
          conid: requestedConid ?? undefined,
        },
      });
    }
  }

  const continuousSymbol = root ? `${root}1!` : '';
  for (const timeframe of contextOrder) {
    if (continuousSymbol) {
      attempts.push({
        assetClass,
        chartMode: 'approximate',
        sourceType: 'continuous_contract',
        symbol: continuousSymbol,
        timeframe,
        exactInstrument: false,
        fallbackReason: exactContractAvailable ? 'exact_contract_unavailable' : 'missing_exact_futures_contract',
      });
    }

    attempts.push({
      assetClass,
      chartMode: 'approximate',
      sourceType: 'context_only',
      symbol: root || requestedSymbol,
      timeframe,
      exactInstrument: false,
      fallbackReason: exactContractAvailable ? 'exact_contract_unavailable' : 'missing_exact_futures_contract',
    });
  }

  return executeAttempts({
    attempts,
    assetClass,
    requestedSymbol,
    requestedTimeframe,
    exactContract: input.exactContract,
    fetchBars: input.fetchBars,
    unavailableFallbackReason: exactContractAvailable ? 'exact_contract_unavailable' : 'missing_exact_futures_contract',
  });
}

async function resolveNonFuturesTradeChartSource<TCandle>(input: ResolveTradeChartSourceInput<TCandle>): Promise<TradeChartLoadResult<TCandle>> {
  const assetClass = normalizeTradeChartAssetClass(input.assetClass);
  const requestedSymbol = String(input.requestedSymbol || '').trim().toUpperCase();
  const requestedTimeframe = normalizeTradeChartTimeframe(input.requestedTimeframe);

  if (isIndexOrCfdInstrument(requestedSymbol, input.instrumentType)) {
    const unavailable = buildResolution({
      assetClass,
      requestedSymbol,
      resolvedSymbol: requestedSymbol,
      requestedTimeframe,
      resolvedTimeframe: requestedTimeframe,
      chartMode: 'unavailable',
      exactInstrument: false,
      barsAvailable: false,
      fallbackReason: 'index_chart_data_unavailable',
      sourceType: 'none',
    });
    return { resolution: unavailable, candles: [], payload: null };
  }

  const authoritativeOrder = getTimeframeFallbackOrder(assetClass, requestedTimeframe, false);
  const contextOrder = getTimeframeFallbackOrder(assetClass, requestedTimeframe, true).filter((tf) => tf === '1d');
  const attempts: TradeChartFetchAttempt[] = [];

  for (const timeframe of authoritativeOrder) {
    attempts.push({
      assetClass,
      chartMode: 'authoritative',
      sourceType: timeframe === requestedTimeframe ? 'exact_contract' : 'same_symbol_coarser_tf',
      symbol: requestedSymbol,
      timeframe,
      exactInstrument: true,
      fallbackReason: timeframe === requestedTimeframe ? null : 'same_symbol_coarser_timeframe',
    });
  }

  for (const timeframe of contextOrder) {
    attempts.push({
      assetClass,
      chartMode: 'approximate',
      sourceType: 'context_only',
      symbol: requestedSymbol,
      timeframe,
      exactInstrument: true,
      fallbackReason: 'intraday_unavailable_context_daily',
    });
  }

  return executeAttempts({
    attempts,
    assetClass,
    requestedSymbol,
    requestedTimeframe,
    exactContract: input.exactContract,
    fetchBars: input.fetchBars,
    unavailableFallbackReason: 'no_bars_available',
  });
}

export async function resolveTradeChartSource<TCandle>(input: ResolveTradeChartSourceInput<TCandle>): Promise<TradeChartLoadResult<TCandle>> {
  const assetClass = normalizeTradeChartAssetClass(input.assetClass);
  if (assetClass === 'FUT') return resolveFuturesTradeChartSource(input);
  return resolveNonFuturesTradeChartSource(input);
}
